<template>
    <Account
            class="v-reset-password c-account--resetpassword"
            :image-header1="$ucfirst($t('login.reset.forgot'))"
            :image-header2="$ucfirst($t('login.reset.choosenew'))"
            :image-url="$asset('image/reset-password-stock.jpg')"
            :image-aspect-ratio="1054/728"
            :break-after-logo="true"
            :versionInfo='$app.version'
    >
        <h1 v-show="$vuetify.breakpoint.mdAndUp"
            class="v-reset-password__choosenew text-h5 text-center font-family-futura">{{
            $ucfirst($t('login.reset.choosenewpassword')) }}</h1>

        <v-form v-on:submit.prevent="resetPassword">
            <v-row class="v-reset-password__validations text-check">
                <v-col cols="6 pt-0">
                    <p :class="{'text-check' : true, 'text-check--marked' : checks.mincharactersValid}">
                        <eva-icon v-bind="iconProps(checks.mincharactersValid)" />
                        {{ $ucfirst($t('validation.mincharacters', { params : { mincharacters: 8 } })) }}
                    </p>
                    <p :class="{'text-check' : true, 'text-check--marked' : checks.lowercaseValid}">
                        <eva-icon v-bind="iconProps(checks.lowercaseValid)" />
                        {{ $ucfirst($t('validation.lowercase')) }}
                    </p>
                    <p :class="{'text-check' : true, 'text-check--marked' : checks.specialsymbolValid}">
                        <eva-icon v-bind="iconProps(checks.specialsymbolValid)" />
                        {{ $ucfirst($t('validation.specialsymbol')) }}
                    </p>
                </v-col>
                <v-col cols="6 pt-0">
                    <p :class="{'text-check' : true, 'text-check--marked' : checks.uppercaseValid}">
                        <eva-icon v-bind="iconProps(checks.uppercaseValid)" />
                        {{ $ucfirst($t('validation.uppercase')) }}
                    </p>
                    <p :class="{'text-check' : true, 'text-check--marked' : checks.numberValid}">
                        <eva-icon v-bind="iconProps(checks.numberValid)" />
                        {{ $ucfirst($t('validation.number')) }}
                    </p>
                </v-col>
            </v-row>
            <p v-if="passwordResetMessage">
                {{ passwordResetMessage }}
            </p>
            <v-text-field
                    v-else
                    @change="evaluatePassword"
                    @keyup="evaluatePassword"
                    v-model="password"
                    :label="$capitalize($t('login.password'))"
                    autocomplete="on"
                    outlined
                    :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                    @click:append="() => (showPassword = !showPassword)"
                    :type="showPassword ? 'text' : 'password'"
            ></v-text-field>

            <v-progress-linear
                    class="v-reset-password__progress" :value="passwordStrength"
                    :color="barColor"
                    :background-color="[barColor, 'lighten-3'].join(' ')"
            ></v-progress-linear>

            <v-btn :disabled="!passwordIsValid" color="primary" type="submit" x-large block>
                {{ $capitalize($t('buttons.save', {catalog: 'app_mobile'})) }}
            </v-btn>

            <p class="v-reset-password__backtologin">
                <router-link class="inline-stretch text-center secondary--text text-decoration-underline" to="/login">{{
                    $ucfirst($t('login.backtologin')) }}
                </router-link>
            </p>
        </v-form>
    </Account>
</template>

<script>
import Account from "../../components/Account/Account";

/**
 * Component ResetPassword.vue
 *
 * @module ResetPassword
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [deprecated] app/src/APP_DASHBOARD/templates/views/Account/ResetPassword.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'ResetPassword',
  components: {
    Account
  },
  props: ['token'],
  mounted() {
    this.evaluatePassword()
  },
  methods: {
    resetPassword: function () {
      if (this.resetPasswordPromise !== null)
        return this.resetPasswordPromise

      if (!this.passwordIsValid) return Promise.resolve(false)

      this.resetPasswordPromise = this.$app.authentication.resetPassword(
        this.token,
        this.password,
      ).then((result) => {
        if (result.response && result.response.status === 200){
          this.passwordResetMessage = this.$ucfirst(this.$t('login.reset.passwordupdated'))
          this.$toasted.show(this.passwordResetMessage)
        }
        this.resetPasswordPromise = null;
      }).catch(result => {
        if (result.response && result.response.status === 403){
          this.passwordResetMessage = this.$ucfirst(this.$t('login.reset.passwordupdatedexpired'))
        }else{
          this.passwordResetMessage = this.$ucfirst(this.$t('login.reset.passwordupdatedproblem'))
        }
        this.$toasted.error(this.passwordResetMessage)
        this.resetPasswordPromise = null;
      })
    },
    iconProps(isValid) {
      return {
        name : isValid ? 'checkmark' : 'close',
        fill : isValid ? '#81e043' : this.$color.coro.red
      }
    },
    evaluatePassword(){
      this.checks.mincharactersValid = this.password.length >= 8
      this.checks.uppercaseValid = this.password && this.password.match(/[A-Z]/g)
      this.checks.lowercaseValid = this.password && this.password.match(/[a-z]/g)
      this.checks.numberValid = this.password && this.password.match(/[0-9]/g)
      this.checks.specialsymbolValid = this.password && this.password.match(/[^\w\s]/g)

      let totalCheckCount = 0, validCheckCount = 0;

      for (let checkKey in this.checks){
        totalCheckCount++;
        if (this.checks[checkKey]) validCheckCount++
      }

      this.passwordStrength = totalCheckCount ? Math.round(validCheckCount/totalCheckCount * 100) : 100;

      return this.passwordIsValid = (totalCheckCount === validCheckCount)
    }
  },
  data: () => ({
    checks: {
      mincharactersValid: false,
      lowercaseValid: false,
      specialsymbolValid: false,
      uppercaseValid: false,
      numberValid: false,
    },
    passwordResetMessage: null,
    password: '',
    passwordIsValid: false,
    showPassword: false,
    passwordStrength: 0,
    resetPasswordPromise: null,
  }),
  computed: {
    barColor: function () {
      if (this.passwordStrength <= 20) return "red"
      if (this.passwordStrength <= 40) return "orange"
      if (this.passwordStrength <= 60) return "amber"
      if (this.passwordStrength <= 80) return "light-green"

      return "green"
    }
  }
}
</script>

<style lang="scss">
    // mobile first
    .v-reset-password__choosenew {
        margin: 50px 0 76px 0;
    }

    .v-reset-password__backtologin {
        margin-top: 20px;
    }

    .v-reset-password__validations {
        p {
            margin-bottom: 8px;
        }

        svg {
            width: 0.9em;
            height: 0.9em;
        }
    }

    .v-reset-password__progress{
        margin-bottom: 40px;
    }
</style>